import { useState, useCallback, useEffect } from "react";
import Navbar from "../components/Navbar";
import {
  ChevronDown, ChevronRight, ChevronLeft, Sparkles, Search, Zap, Gift, Target,
  MessageCircle, PenTool, Lightbulb, CheckCircle2, XCircle, Clipboard, Check,
  Rocket, ShieldCheck, Brain, Eye, RotateCw, TrendingUp, ArrowRight,
  Briefcase, Home, GraduationCap, Palette, Trophy, Copy,
} from "lucide-react";

/* ============================================================
   KI-Lernen — adaptiert vom "KI-Abenteuer Guide" im [KI]NPUT Stil
   Dark / Cyan / Sharp edges / Outfit-Typo / Liquid-Glass
============================================================ */

const STEP_LABELS = [
  "Willkommen",
  "Die Tools",
  "Prompts",
  "Anwendungen",
  "Tipps & Check",
];

const MOOD_TEXTS = {
  overwhelmed: "Das ist völlig normal. KI ist neu und das fühlt sich groß an. Aber hier lernst du Schritt für Schritt – ohne Druck, ohne Vorwissen. Du bist genau richtig hier.",
  curious: "Das ist der perfekte Ausgangspunkt. Neugier ist alles was du brauchst. Mach einfach weiter – jede Seite bringt dich einem echten Ergebnis näher.",
  ready: "Perfekt – dann nichts wie los. Klick gleich auf „Los geht's\" und du hast in 5 Minuten dein erstes echtes Ergebnis in der Hand.",
};

const QUIZ_DATA = {
  q0: {
    q: "Was ist der einzige echte Fehler den du beim Start mit KI machen kannst?",
    opts: [
      { t: "Den falschen Prompt schreiben", c: false },
      { t: "Ein kostenpflichtiges Abo wählen", c: false },
      { t: "Gar nicht anfangen", c: true },
      { t: "Zu viele Tools gleichzeitig testen", c: false },
    ],
    good: "Genau! Der einzige echte Fehler ist Nicht-Starten. Alles andere lässt sich korrigieren.",
    bad: "Fast – aber der wirkliche Fehler ist noch grundlegender.",
  },
  q1: {
    q: "Was ist der beste Weg herauszufinden welches KI-Tool am besten zu dir passt?",
    opts: [
      { t: "Den teuersten Plan kaufen", c: false },
      { t: "Allen dieselbe Frage stellen und vergleichen", c: true },
      { t: "Warten bis jemand eine Empfehlung gibt", c: false },
      { t: "Das bekannteste Tool nehmen und nie wechseln", c: false },
    ],
    good: "Richtig! Ausprobieren und vergleichen ist der schnellste Weg zum Lieblingstool.",
    bad: "Nicht ganz. Der beste Weg ist direktes Testen.",
  },
  q2: {
    q: "Was macht einen guten Prompt aus?",
    opts: [
      { t: "Möglichst kurz und knapp", c: false },
      { t: "Möglichst viele Fachbegriffe", c: false },
      { t: "Kontext + klare Aufgabe + gewünschtes Format", c: true },
      { t: "Auf Englisch schreiben", c: false },
    ],
    good: "Perfekt! Kontext + Aufgabe + Format – das ist die Formel für gute Prompts.",
    bad: "Nicht ganz. Es geht um Struktur, nicht um Kürze oder Sprache.",
  },
  q3: {
    q: "Was solltest du immer tun bevor du einen KI-Text weiterverwendest?",
    opts: [
      { t: "Nichts – KI macht keine Fehler", c: false },
      { t: "Kurz drüber lesen und kritisch prüfen", c: true },
      { t: "Den Text ins Englische übersetzen", c: false },
      { t: "Drei verschiedene KI-Tools fragen", c: false },
    ],
    good: "Genau! KI kann Fehler machen. Kurz drüber lesen schützt vor Peinlichkeiten.",
    bad: "Noch nicht ganz. KI ist gut – aber nicht unfehlbar.",
  },
  q4: {
    q: "Wie verbesserst du dich am schnellsten im Umgang mit KI?",
    opts: [
      { t: "Viele YouTube-Videos schauen", c: false },
      { t: "Zuerst alles über KI lesen", c: false },
      { t: "Einfach täglich ausprobieren – mit echten Aufgaben", c: true },
      { t: "Das teuerste Abo kaufen", c: false },
    ],
    good: "Absolut richtig! Tägliches Ausprobieren mit echten Aufgaben ist der einzige Lernweg der wirklich wirkt.",
    bad: "Theorie hilft – aber Praxis schlägt alles.",
  },
};

/* ---------- Small primitives ---------- */

const IMG_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/lernen`;

function LernenImage({ file, alt, caption, height = "h-[260px] sm:h-[340px] lg:h-[420px]", overlayPos = "left", testid, gradient = "from-black via-black/60 to-transparent" }) {
  const [hover, setHover] = useState(false);
  const gradientClass =
    overlayPos === "bottom" ? "bg-gradient-to-t from-black/90 via-black/20 to-transparent"
    : overlayPos === "none" ? ""
    : `bg-gradient-to-r ${gradient}`;
  return (
    <figure
      className={`relative overflow-hidden border border-white/[0.07] bg-black group lernen-reveal`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* floating cyan glow that follows hover */}
      <div
        className={`pointer-events-none absolute inset-0 z-10 transition-opacity duration-500 ${hover ? "opacity-100" : "opacity-0"}`}
        style={{ background: "radial-gradient(circle at 30% 40%, rgba(0,229,255,0.18) 0%, transparent 55%)" }}
      />
      <img
        src={`${IMG_BASE}/${file}`}
        alt={alt}
        loading="lazy"
        data-testid={testid}
        className={`w-full ${height} object-cover transition-all duration-[900ms] ease-out ${hover ? "scale-[1.04] brightness-110" : "scale-100 brightness-90"}`}
      />
      {gradientClass && <div className={`absolute inset-0 pointer-events-none ${gradientClass}`} />}
      {caption}
      {/* corner tick */}
      <div className="absolute top-3 right-3 w-8 h-8 border-t border-r border-[#00e5ff]/40 pointer-events-none" />
      <div className="absolute bottom-3 left-3 w-8 h-8 border-b border-l border-[#00e5ff]/40 pointer-events-none" />
    </figure>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <span className="w-8 h-px bg-[#00e5ff]" />
      <span className="font-outfit text-[11px] uppercase tracking-[0.32em] text-[#00e5ff]/80 font-semibold">
        {children}
      </span>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[0.95] text-white mb-3">
      {children}
    </h2>
  );
}

function SectionSub({ children }) {
  return <p className="text-base sm:text-lg text-white/55 leading-relaxed mb-8">{children}</p>;
}

function Accordion({ title, children, defaultOpen = false, id }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/[0.07] bg-white/[0.015] mb-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`lernen-acc-${id}`}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/[0.02] transition-colors"
      >
        <span className="text-[14px] sm:text-[15px] font-outfit font-semibold text-white/90">{title}</span>
        <ChevronDown size={16} className={`text-[#00e5ff]/80 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 text-[14px] text-white/65 leading-relaxed border-t border-white/[0.04]">
          {children}
        </div>
      )}
    </div>
  );
}

function Quiz({ id, onCorrect }) {
  const data = QUIZ_DATA[id];
  const [picked, setPicked] = useState(null);
  const correct = picked !== null && data.opts[picked].c;
  return (
    <div className="border border-[#00e5ff]/20 bg-gradient-to-br from-[#00e5ff]/[0.03] to-transparent p-6 sm:p-7 mt-10">
      <div className="flex items-center gap-2 mb-1.5">
        <Target size={15} className="text-[#00e5ff]" />
        <h3 className="font-outfit font-bold text-[15px] text-white uppercase tracking-[0.12em]">Kurze Wissensrunde</h3>
      </div>
      <div className="text-[12px] text-white/40 mb-5">1 Frage · kein Druck</div>
      <div className="text-[15px] sm:text-base text-white mb-5 font-medium leading-snug">{data.q}</div>
      <div className="grid gap-2">
        {data.opts.map((o, i) => {
          const isPicked = picked === i;
          const state = picked === null ? "idle" : isPicked ? (o.c ? "correct" : "wrong") : "disabled";
          const base = "text-left px-4 py-3 text-[14px] font-medium border transition-colors font-outfit";
          const styles = {
            idle: "border-white/10 bg-black text-white/80 hover:border-[#00e5ff]/50 hover:bg-[#00e5ff]/[0.04] cursor-pointer",
            correct: "border-[#2ecc71]/60 bg-[#2ecc71]/10 text-[#2ecc71] cursor-default",
            wrong: "border-[#ef4444]/60 bg-[#ef4444]/10 text-[#ef4444] cursor-default",
            disabled: "border-white/5 bg-black/50 text-white/30 cursor-default",
          };
          return (
            <button
              key={o.t}
              disabled={picked !== null}
              onClick={() => { setPicked(i); if (o.c) onCorrect?.(); }}
              data-testid={`lernen-quiz-${id}-opt-${i}`}
              className={`${base} ${styles[state]}`}
            >
              <span className="inline-flex items-center gap-2">
                {state === "correct" && <CheckCircle2 size={14} />}
                {state === "wrong" && <XCircle size={14} />}
                {o.t}
              </span>
            </button>
          );
        })}
      </div>
      {picked !== null && (
        <div
          className={`mt-5 text-[13.5px] leading-relaxed px-4 py-3 border-l-2 ${correct ? "border-[#2ecc71] text-[#2ecc71]/90 bg-[#2ecc71]/5" : "border-[#ef4444] text-[#ef4444]/90 bg-[#ef4444]/5"}`}
          data-testid={`lernen-quiz-${id}-feedback`}
        >
          {correct ? data.good : data.bad}
        </div>
      )}
    </div>
  );
}

/* ---------- Step components ---------- */

function Step0({ goNext }) {
  const [mood, setMood] = useState(null);
  return (
    <div>
      {/* Hero */}
      <div className="relative border border-white/[0.07] bg-gradient-to-br from-[#00e5ff]/[0.04] via-transparent to-transparent p-8 sm:p-12 mb-12 overflow-hidden">
        <div className="absolute top-0 right-0 w-[50%] h-[80%] rounded-full blur-[120px] pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,229,255,0.08) 0%, transparent 70%)" }} />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#00e5ff]/30 bg-[#00e5ff]/5 mb-6">
            <Sparkles size={12} className="text-[#00e5ff]" />
            <span className="font-outfit text-[10.5px] uppercase tracking-[0.28em] text-[#00e5ff] font-semibold">
              Kein Vorwissen · Kostenlos · Auf Deutsch
            </span>
          </div>
          <h1 className="font-outfit font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.92] text-white mb-5">
            Du stehst vor einer<br />neuen Welt.<br />
            <span className="text-[#00e5ff]">Trau dich rein.</span>
          </h1>
          <p className="text-base sm:text-lg text-white/55 leading-relaxed max-w-2xl mb-8">
            KI klingt nach Wissenschaft – ist aber <strong className="text-white">einfacher als du denkst</strong>. Kein Code. Kein Studium. Nur Neugier.
          </p>
          <div className="flex flex-wrap gap-3 mb-12">
            <button onClick={goNext} data-testid="lernen-hero-start" className="inline-flex items-center gap-2 px-6 py-3 bg-[#00e5ff] text-black font-outfit font-bold text-[13px] uppercase tracking-[0.14em] hover:bg-white transition-colors">
              Los geht's <ArrowRight size={14} />
            </button>
          </div>

          {/* Mood picker */}
          <div className="border-t border-white/[0.07] pt-8">
            <div className="font-outfit text-[13px] uppercase tracking-[0.22em] text-white/55 font-semibold mb-4">
              Wie fühlst du dich gerade bei dem Thema KI?
            </div>
            <div className="flex flex-wrap gap-3 mb-5">
              {[
                { k: "overwhelmed", label: "Überwältigt" },
                { k: "curious", label: "Neugierig" },
                { k: "ready", label: "Bereit!" },
              ].map((m) => (
                <button
                  key={m.k}
                  onClick={() => setMood(m.k)}
                  data-testid={`lernen-mood-${m.k}`}
                  className={`px-5 py-2.5 text-[13px] font-outfit font-semibold border transition-colors ${mood === m.k ? "border-[#00e5ff] bg-[#00e5ff]/10 text-[#00e5ff]" : "border-white/15 bg-black text-white/70 hover:border-white/35"}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {mood && (
              <p className="text-[14px] text-white/70 leading-relaxed border-l-2 border-[#00e5ff] pl-4 max-w-xl" data-testid="lernen-mood-response">
                {MOOD_TEXTS[mood]}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Atmospheric image banner — "Einfach anfangen." */}
      <LernenImage
        file="willkommen.png"
        alt="Entspannt am Laptop – so einfach startet KI."
        testid="lernen-img-hero"
        overlayPos="left"
        caption={
          <figcaption className="absolute left-0 bottom-0 p-6 sm:p-8 max-w-md z-20">
            <div className="flex items-center gap-3 mb-2">
              <span className="w-6 h-px bg-[#00e5ff]" />
              <span className="font-outfit text-[10px] uppercase tracking-[0.28em] text-[#00e5ff]/80 font-semibold">Kein Sci-Fi</span>
            </div>
            <div className="font-outfit font-black text-xl sm:text-2xl tracking-tight leading-tight text-white">
              Nur du, ein Laptop,<br />und eine <span className="text-[#00e5ff]">neue Art</span> zu fragen.
            </div>
          </figcaption>
        }
      />
      <div className="mb-16" />

      {/* Feature cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.05] mb-16 border border-white/[0.06] lernen-stagger">
        {[
          { icon: Search, title: "Was ist KI?", text: "Ein Programm das mit dir spricht und echte Aufgaben löst – in deiner Sprache." },
          { icon: Zap, title: "Wie schnell?", text: "In 5 Minuten bist du angemeldet und hast dein erstes Ergebnis." },
          { icon: Gift, title: "Kostet das?", text: "Nein. Die wichtigsten Tools sind kostenlos. Nur eine E-Mail nötig." },
          { icon: Target, title: "Wofür?", text: "Texte, Ideen, Planung, Antworten – alles was dir täglich Zeit kostet." },
        ].map(({ icon: Icon, title, text }) => (
          <div key={title} className="bg-black p-6 hover:bg-white/[0.02] transition-colors">
            <Icon size={18} className="text-[#00e5ff] mb-3" strokeWidth={1.6} />
            <div className="font-outfit font-bold text-[15px] text-white mb-2">{title}</div>
            <div className="text-[13px] text-white/50 leading-relaxed">{text}</div>
          </div>
        ))}
      </div>

      <SectionLabel>Das Wichtigste zuerst</SectionLabel>
      <SectionTitle>KI ist kein Roboter.<br /><span className="text-[#00e5ff]">Sie ist ein Gesprächspartner.</span></SectionTitle>
      <SectionSub>Du schreibst. Sie antwortet. Du sagst was dir gefällt. Sie passt sich an. Kein Geheimwissen nötig.</SectionSub>

      <div className="space-y-0">
        <Accordion id="was" title="Was steckt hinter KI? Kurze Erklärung ohne Technik">
          KI-Chatbots wurden mit riesigen Mengen Text trainiert. Dadurch können sie Sprache verstehen und sinnvoll antworten. <strong className="text-white">Du musst das nicht verstehen um es zu nutzen</strong> – genauso wenig wie du weißt wie ein Auto funktioniert um Auto zu fahren.
          <br /><br />
          Was du wissen solltest: Die KI hat kein Gedächtnis zwischen verschiedenen Gesprächen. Jede neue Unterhaltung fängt bei null an. Innerhalb eines Gesprächs erinnert sie sich an alles.
        </Accordion>
        <Accordion id="angst" title='"Ich mache bestimmt etwas falsch" – dein größtes Hindernis'>
          Es gibt kein Richtig oder Falsch. Wenn die Antwort nicht passt, schreibst du: <strong className="text-white">„Probier's nochmal anders."</strong> Die KI bewertet dich nicht. Sie wird nicht ungeduldig. Du kannst hundertmal fragen ohne peinlich zu sein.
          <br /><br />
          Der einzige Fehler den du machen kannst: <strong className="text-white">gar nicht anfangen.</strong>
        </Accordion>
        <Accordion id="daten" title="Datenschutz – was sollte ich wissen?">
          Was du eingibst wird an Server übermittelt. <strong className="text-white">Keine Passwörter, keine Kontonummern, keine privaten Kundendaten.</strong> Für allgemeine Texte, Ideen und Entwürfe kein Problem. Im Zweifel Namen anonymisieren.
        </Accordion>
      </div>

      <Quiz id="q0" />
    </div>
  );
}

function Step1() {
  const TOOLS = [
    { icon: MessageCircle, name: "ChatGPT", maker: "OpenAI", url: "chat.openai.com", desc: "Das bekannteste KI-Tool der Welt. Riesige Community, viele Tutorials. Ideal zum Start weil so viel Hilfe verfügbar ist." },
    { icon: Sparkles, name: "Gemini", maker: "Google", url: "gemini.google.com", desc: "Googles KI. Stark bei Recherche und ideal wenn du Gmail oder Google Docs nutzt. Nahtlose Integration." },
    { icon: Zap, name: "Copilot", maker: "Microsoft", url: "copilot.microsoft.com", desc: "Direkt in Windows und Office integriert. Wenn du schon Microsoft 365 nutzt: kein Extra-Account nötig." },
    { icon: Brain, name: "Claude", maker: "Anthropic", url: "claude.ai", desc: "Bekannt für besonders präzise und gut strukturierte Antworten. Stark bei langen Texten und Analyse." },
  ];
  const TABLE = [
    ["ChatGPT", "Ja", "Ja", "Ja", "Sehr gut"],
    ["Gemini", "Ja", "Ja", "Ja", "Sehr gut"],
    ["Copilot", "Ja", "Ja", "Ja", "Mit MS-Konto"],
    ["Claude", "Ja", "Ja", "Ja", "Sehr gut"],
  ];
  return (
    <div>
      <SectionLabel>Die Werkzeugkiste</SectionLabel>
      <SectionTitle>Welches Tool soll ich nehmen?</SectionTitle>
      <SectionSub>Es gibt mehrere gute Optionen – alle kostenlos zum Starten. Probiere aus welches sich für dich richtig anfühlt.</SectionSub>

      {/* Tools image banner */}
      <LernenImage
        file="tools.png"
        alt="Vier Werkzeuge stehen für dich bereit — du hast die Wahl."
        testid="lernen-img-tools"
        overlayPos="bottom"
        height="h-[200px] sm:h-[260px] lg:h-[300px]"
        caption={
          <figcaption className="absolute left-0 right-0 bottom-0 p-5 text-center z-20">
            <p className="text-[13px] text-white/75 font-outfit">
              Vier Werkzeuge. <span className="text-[#00e5ff] font-semibold">Du hast die Wahl.</span>
            </p>
          </figcaption>
        }
      />
      <div className="mb-10" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/[0.05] mb-8 border border-white/[0.06] lernen-stagger">
        {TOOLS.map(({ icon: Icon, name, maker, url, desc }) => (
          <div key={name} className="bg-black p-6 hover:bg-white/[0.02] transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 border border-[#00e5ff]/30 bg-[#00e5ff]/5 flex items-center justify-center">
                <Icon size={18} className="text-[#00e5ff]" strokeWidth={1.6} />
              </div>
              <div>
                <div className="font-outfit font-bold text-[16px] text-white leading-tight">{name}</div>
                <div className="text-[11px] text-white/40 uppercase tracking-wider font-outfit">{maker} · {url}</div>
              </div>
            </div>
            <p className="text-[13.5px] text-white/60 leading-relaxed mb-3">{desc}</p>
            <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-outfit font-semibold text-[#2ecc71] border border-[#2ecc71]/40 bg-[#2ecc71]/5">
              Kostenlos & Premium
            </span>
          </div>
        ))}
      </div>

      <div className="border-l-2 border-[#00e5ff] bg-[#00e5ff]/[0.04] p-5 mb-12">
        <div className="flex items-start gap-3">
          <Lightbulb size={18} className="text-[#00e5ff] shrink-0 mt-0.5" strokeWidth={1.8} />
          <p className="text-[14px] text-white/80 leading-relaxed">
            <strong className="text-white">Tipp:</strong> Stell allen vier dieselbe Frage – z.B. <em className="text-[#00e5ff]/90">„Erkläre mir in 3 Sätzen wie das Internet funktioniert."</em> Das Tool das dir am besten antwortet wird dein Favorit.
          </p>
        </div>
      </div>

      <div className="mb-12">
        <div className="font-outfit text-[13px] uppercase tracking-[0.22em] text-white/55 font-semibold mb-4">
          Tool-Vergleich auf einen Blick
        </div>
        <div className="overflow-x-auto border border-white/[0.07]">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-white/[0.03]">
                {["Tool", "Kostenlos?", "Auf Deutsch?", "Nur im Browser?", "Für Einsteiger?"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 font-outfit text-[11px] uppercase tracking-[0.16em] text-white/55 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TABLE.map((row, i) => (
                <tr key={row[0]} className={i < TABLE.length - 1 ? "border-b border-white/[0.05]" : ""}>
                  <td className="px-4 py-3 font-outfit font-bold text-white">{row[0]}</td>
                  {row.slice(1).map((cell, j) => (
                    <td key={`${row[0]}-${j}`} className="px-4 py-3">
                      <span className={cell === "Mit MS-Konto" ? "text-[#f39c12]" : "text-[#2ecc71]"}>
                        <Check size={14} className="inline mr-1" /> {cell}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-12">
        <div className="font-outfit text-[13px] uppercase tracking-[0.22em] text-white/55 font-semibold mb-5">
          In 3 Schritten loslegen
        </div>
        {[
          { n: 1, t: "Tool öffnen", d: "Geh auf eine der Websites – kein Download nötig, läuft direkt im Browser. Auf dem Smartphone genauso.", tip: "Als Lesezeichen speichern – du wirst es häufiger besuchen als du denkst." },
          { n: 2, t: "Account erstellen", d: "„Sign Up\" klicken → E-Mail eingeben → Passwort → Bestätigungsmail öffnen → fertig. Dauert 3 Minuten.", tip: "Normale E-Mail reicht. Keine Kreditkarte für die kostenlose Version." },
          { n: 3, t: "Erste Nachricht tippen", d: "Unten im Fenster ist ein Textfeld. Auf Deutsch schreiben was dir in den Sinn kommt. Die KI antwortet sofort.", tip: "Erste Testnachricht: „Hallo! Ich bin neu hier. Was kannst du für mich tun?\"" },
        ].map((s) => (
          <div key={s.n} className="flex gap-5 py-5 border-b border-white/[0.05] last:border-b-0">
            <div className="shrink-0 w-10 h-10 border border-[#00e5ff]/40 bg-[#00e5ff]/5 flex items-center justify-center font-outfit font-black text-[#00e5ff] text-[17px]">
              {s.n}
            </div>
            <div className="flex-1">
              <div className="font-outfit font-bold text-white text-[15px] mb-1">{s.t}</div>
              <p className="text-[14px] text-white/60 leading-relaxed mb-2">{s.d}</p>
              <div className="inline-flex items-start gap-2 text-[13px] text-[#00e5ff]/75 bg-[#00e5ff]/[0.04] border border-[#00e5ff]/15 px-3 py-2">
                <Lightbulb size={13} className="shrink-0 mt-0.5" />
                <span>{s.tip}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Accordion id="kosten" title="Brauche ich wirklich ein Abo?">
        <strong className="text-white">Nein – nicht zum Starten.</strong> Die kostenlose Version reicht für Einsteiger vollständig aus. Bezahlt lohnt sich erst wenn du täglich damit arbeitest und an Grenzen stößt. Fang kostenlos an.
      </Accordion>
      <Accordion id="geraet" title="Nur am Computer – oder auch am Handy?">
        Alle Tools laufen auf <strong className="text-white">jedem Gerät mit Browser</strong> – Computer, Tablet, Smartphone. Die meisten haben auch Apps für iOS und Android. Du brauchst nur eine Internetverbindung.
      </Accordion>

      <Quiz id="q1" />
    </div>
  );
}

function Step2() {
  const [rolle, setRolle] = useState("");
  const [aufgabe, setAufgabe] = useState("");
  const [thema, setThema] = useState("");
  const [format, setFormat] = useState("");
  const [copied, setCopied] = useState(false);

  const parts = [];
  if (rolle) parts.push(rolle + ".");
  if (aufgabe && thema) parts.push(aufgabe + " " + thema + ".");
  else if (aufgabe) parts.push(aufgabe + " [Thema ergänzen].");
  else if (thema) parts.push("Hilf mir beim Thema: " + thema + ".");
  if (format) parts.push(format);
  const prompt = parts.join(" ");
  const hasAny = rolle || aufgabe || thema;

  const onCopy = useCallback(() => {
    if (!prompt) return;
    navigator.clipboard.writeText(prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }, [prompt]);

  const selectCls = "w-full bg-black border border-white/10 px-4 py-3 text-[14px] text-white font-outfit focus:border-[#00e5ff]/60 focus:outline-none appearance-none";

  return (
    <div>
      <SectionLabel>Die Kunst des Fragens</SectionLabel>
      <SectionTitle>Ein Prompt ist einfach<br />das was du <span className="text-[#00e5ff]">eintippst.</span></SectionTitle>
      <SectionSub>Kein Trick, kein Code. Aber wie du fragst macht den Unterschied zwischen einer mittelmäßigen und einer fantastischen Antwort.</SectionSub>

      {/* Formula */}
      <div className="flex flex-wrap items-center gap-2 mb-12">
        {[
          { l: "Wer bist du?", c: "#00e5ff" },
          { l: "Was soll es sein?", c: "#00e5ff" },
          { l: "Format / Länge", c: "#00e5ff" },
        ].map((p, i, arr) => (
          <div key={p.l} className="flex items-center gap-2">
            <div className="px-4 py-3 border bg-white/[0.02] text-[13px] font-outfit font-semibold text-white" style={{ borderColor: "rgba(0,229,255,0.3)" }}>
              {p.l}
            </div>
            <span className="text-white/30 text-xl font-black">{i < arr.length - 1 ? "+" : "="}</span>
          </div>
        ))}
        <div className="px-5 py-3 bg-[#00e5ff] text-black text-[13px] font-outfit font-bold uppercase tracking-[0.12em]">
          Gute Antwort
        </div>
      </div>

      {/* Visual metaphor image */}
      <LernenImage
        file="prompts.png"
        alt="Drei Bausteine verbinden sich zu einer großartigen Antwort — visuelle Metapher für einen guten Prompt."
        testid="lernen-img-prompts"
        overlayPos="bottom"
        height="h-[220px] sm:h-[300px] lg:h-[360px]"
        caption={
          <figcaption className="absolute left-0 right-0 bottom-0 p-5 sm:p-6 text-center z-20">
            <p className="text-[13px] sm:text-[14px] text-white/80 font-outfit max-w-xl mx-auto">
              Je mehr Struktur du gibst, desto <span className="text-[#00e5ff] font-semibold">klarer wird das Ergebnis</span> auf der anderen Seite.
            </p>
          </figcaption>
        }
      />
      <div className="mb-14" />

      <div className="space-y-0 mb-14">
        <Accordion id="p1" title="Beispiel 1: Eine E-Mail schreiben" defaultOpen>
          <span className="text-[#ef4444] font-semibold">✕ Schlecht:</span> <em>„Schreib eine E-Mail."</em>
          <br /><br />
          <span className="text-[#2ecc71] font-semibold">✓ Gut:</span> <em>„Ich bin selbstständiger Handwerker. Ich muss einem Kunden absagen der für Freitag geplant war – Grund ist ein Notfallauftrag. Schreib eine kurze, freundliche Absage-E-Mail. Max. 5 Sätze, mit neuem Terminangebot."</em>
          <br /><br />
          <strong className="text-white">Unterschied:</strong> Kontext (wer bist du), Situation (was passiert), Wunsch (Ton), Format (Länge).
        </Accordion>
        <Accordion id="p2" title="Beispiel 2: Ideen entwickeln">
          <span className="text-[#ef4444] font-semibold">✕ Schlecht:</span> <em>„Gib mir Ideen."</em>
          <br /><br />
          <span className="text-[#2ecc71] font-semibold">✓ Gut:</span> <em>„Ich betreibe einen kleinen Laden für Kunsthandwerk in einer Kleinstadt mit 8.000 Einwohnern. Problem: kaum Stammkunden. Nenn mir 5 kreative Ideen die wenig bis nichts kosten. Kurz und praxisnah."</em>
          <br /><br />
          <strong className="text-white">Unterschied:</strong> Je mehr Kontext, desto spezifischer und nützlicher die Antworten.
        </Accordion>
        <Accordion id="p3" title="Beispiel 3: Etwas erklären lassen">
          <span className="text-[#ef4444] font-semibold">✕ Schlecht:</span> <em>„Erkläre mir Zinsen."</em>
          <br /><br />
          <span className="text-[#2ecc71] font-semibold">✓ Gut:</span> <em>„Erkläre mir Zinsen und Zinseszins als wäre ich 14 Jahre alt. Benutze ein konkretes Beispiel mit echten Zahlen. Maximal 150 Wörter."</em>
          <br /><br />
          <strong className="text-white">Unterschied:</strong> „Als wäre ich 14" – und schon wird es verständlich statt kompliziert.
        </Accordion>
        <Accordion id="p4" title="Beispiel 4: Wenn die Antwort nicht passt">
          Sag einfach was nicht stimmt:
          <br /><br />
          <span className="text-[#2ecc71]">„Das ist zu formell. Schreib es locker."</span><br />
          <span className="text-[#2ecc71]">„Kürzer bitte – maximal 3 Sätze."</span><br />
          <span className="text-[#2ecc71]">„Streich Punkt 3 und ersetze ihn durch etwas Kreativeres."</span>
          <br /><br />
          <strong className="text-white">Du bist im Dialog.</strong> Du musst nie neu anfangen.
        </Accordion>
      </div>

      {/* Prompt Builder */}
      <div className="border border-[#00e5ff]/25 bg-gradient-to-br from-[#00e5ff]/[0.035] to-transparent p-6 sm:p-8 mb-12">
        <div className="flex items-center gap-2 mb-1">
          <PenTool size={16} className="text-[#00e5ff]" />
          <h3 className="font-outfit font-bold text-white text-[16px] uppercase tracking-[0.1em]">Bau deinen ersten Prompt</h3>
        </div>
        <p className="text-[13px] text-white/45 mb-6">Felder ausfüllen → Prompt entsteht automatisch → kopieren → in dein KI-Tool einfügen.</p>

        <div className="grid gap-4">
          <div>
            <label className="block font-outfit text-[11px] uppercase tracking-[0.18em] text-white/55 font-semibold mb-1.5">Wer bist du?</label>
            <select value={rolle} onChange={(e) => setRolle(e.target.value)} data-testid="lernen-pb-rolle" className={selectCls}>
              <option value="">– Wähle –</option>
              <option value="Ich bin Selbstständiger">Selbstständiger</option>
              <option value="Ich bin Handwerker">Handwerker / Handwerkerin</option>
              <option value="Ich bin Angestellter in einem kleinen Betrieb">Angestellter im Kleinstbetrieb</option>
              <option value="Ich bin Elternteil">Elternteil</option>
              <option value="Ich bin Rentner">Rentner / Rentnerin</option>
              <option value="Ich bin Student">Student / Studentin</option>
              <option value="Ich bin Vereinsmitglied und organisiere Veranstaltungen">Vereinsorganisator</option>
            </select>
          </div>
          <div>
            <label className="block font-outfit text-[11px] uppercase tracking-[0.18em] text-white/55 font-semibold mb-1.5">Was brauchst du?</label>
            <select value={aufgabe} onChange={(e) => setAufgabe(e.target.value)} data-testid="lernen-pb-aufgabe" className={selectCls}>
              <option value="">– Was soll die KI tun? –</option>
              <option value="schreibe mir eine kurze E-Mail über">Eine E-Mail schreiben</option>
              <option value="nenn mir 5 kreative Ideen für">5 Ideen entwickeln</option>
              <option value="erstelle eine übersichtliche Checkliste für">Checkliste erstellen</option>
              <option value="erkläre mir einfach und verständlich">Etwas erklären</option>
              <option value="übersetze folgenden Text auf Englisch:">Text übersetzen</option>
              <option value="formuliere das freundlicher und professioneller um:">Text verbessern</option>
              <option value="fasse die wichtigsten Punkte zusammen von">Zusammenfassung</option>
            </select>
          </div>
          <div>
            <label className="block font-outfit text-[11px] uppercase tracking-[0.18em] text-white/55 font-semibold mb-1.5">Thema / Kontext</label>
            <input
              type="text" value={thema} onChange={(e) => setThema(e.target.value)}
              data-testid="lernen-pb-thema"
              placeholder="z.B. Absage eines Termins wegen Krankheit..."
              className="w-full bg-black border border-white/10 px-4 py-3 text-[14px] text-white placeholder:text-white/30 focus:border-[#00e5ff]/60 focus:outline-none"
            />
          </div>
          <div>
            <label className="block font-outfit text-[11px] uppercase tracking-[0.18em] text-white/55 font-semibold mb-1.5">Wie soll das Ergebnis sein?</label>
            <select value={format} onChange={(e) => setFormat(e.target.value)} data-testid="lernen-pb-format" className={selectCls}>
              <option value="">– Format –</option>
              <option value="Maximal 5 Sätze, kurz und direkt.">Kurz (max. 5 Sätze)</option>
              <option value="Freundlich und locker im Ton.">Locker & freundlich</option>
              <option value="Professionell und sachlich.">Professionell</option>
              <option value="Als übersichtliche Liste mit Stichpunkten.">Als Stichpunkte</option>
              <option value="Einfach erklärt, als würde ich es einem Freund sagen.">Einfach erklärt</option>
            </select>
          </div>
        </div>

        <div className="mt-6 border border-white/10 bg-black p-5 relative min-h-[110px]">
          <div className="flex items-center justify-between mb-2">
            <span className="font-outfit text-[10px] uppercase tracking-[0.22em] text-[#00e5ff]/70 font-semibold">Dein Prompt</span>
            {hasAny && (
              <button onClick={onCopy} data-testid="lernen-pb-copy" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-outfit font-semibold uppercase tracking-[0.14em] border border-[#00e5ff]/40 text-[#00e5ff] hover:bg-[#00e5ff]/10 transition-colors">
                {copied ? <><Check size={12} /> Kopiert</> : <><Copy size={12} /> Kopieren</>}
              </button>
            )}
          </div>
          <div className="text-[14px] text-white/85 leading-relaxed font-mono whitespace-pre-wrap" data-testid="lernen-pb-out">
            {prompt || <span className="text-white/30">Füll die Felder oben aus…</span>}
          </div>
        </div>
      </div>

      <Quiz id="q2" />
    </div>
  );
}

function Step3() {
  const TABS = [
    { k: "work", label: "Arbeit", icon: Briefcase },
    { k: "life", label: "Alltag", icon: Home },
    { k: "learn", label: "Lernen", icon: GraduationCap },
    { k: "create", label: "Kreatives", icon: Palette },
  ];
  const DATA = {
    work: [
      ["E-Mail schreiben", "Professionell klingen ohne nachzudenken", "Ich muss einen Kunden fragen ob er seinen Auftrag bis Ende des Monats bestätigen kann. Kurze freundliche E-Mail, nicht drängend."],
      ["Angebot vorbereiten", "Überzeugender Einleitungstext", "Schreib eine Einleitung für ein Angebot an ein mittelständisches Unternehmen. Dienstleistung: Büroausstattung. Ton: professionell aber persönlich."],
      ["Meeting-Agenda", "In Minuten statt Stunden", "Erstelle eine Agenda für ein 45-Min-Meeting zum Thema Jahresplanung. 5 Personen. Mit Zeitangaben."],
      ["Protokoll schreiben", "Aus Stichworten ein sauberes Protokoll", "Hier sind meine Notizen vom Meeting: [deine Stichwörter]. Erstelle daraus ein Protokoll mit Datum, Teilnehmern und Beschlüssen."],
      ["LinkedIn-Post", "Persönlich ohne Marketingsprech", "Schreib einen LinkedIn-Post über [Thema]. Ton: persönlich, ehrlich. Mit einer Frage am Ende die zur Diskussion einlädt."],
      ["Stellenausschreibung", "Gute Bewerber ansprechen", "Wir suchen eine Bürokauffrau für unser 8-köpfiges Team in [Stadt]. Schreib eine einladende Ausschreibung – kein trockenes Anforderungsprofil."],
    ],
    life: [
      ["Rezeptideen", "Aus dem was im Kühlschrank ist", "Im Kühlschrank: Eier, Pasta, Tomaten, Käse, Zwiebeln. 3 Ideen was ich kochen kann – eine davon vegetarisch."],
      ["Geschenkideen", "Für jeden Anlass und Budget", "Geschenk für meinen 65-jährigen Vater. Er gärtnert gerne, liest Krimis, mag keine Technik. Budget ca. 40 Euro."],
      ["Urlaub planen", "Route und Tipps auf einen Blick", "10 Tage Portugal Algarve mit 2 Kindern (7 und 10). Was sehen? Grober Tagesplan."],
      ["Behördenbrief", "Amtsdeutsch vermeiden", "Ich möchte Widerspruch einlegen gegen einen Steuerbescheid. Musterbrief: höflich, sachlich, klar."],
      ["Trainingsplan", "Individuell ohne Geräte", "3x pro Woche, keine Geräte, Anfänger. 4-Wochen-Plan mit klaren Übungen."],
      ["Party planen", "Checkliste bis zur Einladung", "Geburtstagsfeier für 25 Personen im Garten. Komplette Checkliste für die 4 Wochen vorher."],
    ],
    learn: [
      ["Einfach erklärt", "Jedes Thema verständlich", "Erkläre mir was Inflation ist – so einfach dass es mein 12-jähriger Sohn versteht."],
      ["Sprachen üben", "Persönlicher Sprachlehrer", "Ich möchte Englisch verbessern. Stell mir 10 Fragen zu meinem Alltag auf Englisch. Korrigiere meine Antworten kurz."],
      ["Artikel zusammenfassen", "Langer Text, kurze Essenz", "Hier ist ein langer Artikel: [Text]. Fasse die 5 wichtigsten Punkte in je 3 Sätzen zusammen."],
      ["Technik verstehen", "Ohne Fachwissen navigieren", "Mein WLAN-Router zeigt ein rotes Licht. Was kann das bedeuten? Schritt für Schritt erklärt."],
      ["Vokabeln lernen", "Mit Struktur und Beispielen", "Ich lerne Spanisch (Anfänger). 20 Vokabel-Karteikarten für Alltag – Deutsch/Spanisch mit Beispielsatz."],
      ["Fakten checken", "Schnell Hintergrundwissen", "Ich habe gelesen dass [Behauptung]. Stimmt das? Erkläre kurz und sachlich."],
    ],
    create: [
      ["Kurzgeschichte", "Von der Idee zum Text", "Schreib eine Kurzgeschichte (ca. 300 Wörter) über einen alten Briefträger der an seinem letzten Arbeitstag einen ungewöhnlichen Brief findet."],
      ["Brainstorming", "Blockade überwinden", "Ich eröffne ein kleines Café und suche noch einen Namen. 15 Ideen – kreativ, einprägsam, auf Deutsch. Mix aus ernsthaft und witzig."],
      ["Geburtstagskarte", "Persönlich und herzlich", "Meine Schwester wird 40. Warmherziger, leicht humorvoller Text für ihre Geburtstagskarte. Ca. 80 Wörter."],
      ["Rede schreiben", "Hochzeit, Jubiläum, Abschied", "Mein bester Freund heiratet. Trauzeugenrede ca. 3 Minuten. Warmherzig, leicht witzig. Details: [was du ergänzt]."],
      ["Bildidee beschreiben", "Für Canva, Midjourney & Co.", "Beschreibe ein Bild für Social-Media zu [Thema]. Stil: warm, modern. So detailliert dass jemand es malen könnte."],
      ["Liedtext", "Für Feiern und Partys", "Liedtext für Abschiedsfeier meiner Kollegin auf die Melodie von [Lied]. Persönlich, witzig, mit Bezug zum Arbeitsalltag."],
    ],
  };
  const [tab, setTab] = useState("work");
  const [copiedIdx, setCopiedIdx] = useState(null);

  const copyPrompt = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1500);
  };

  return (
    <div>
      <SectionLabel>Was du damit tun kannst</SectionLabel>
      <SectionTitle>KI im <span className="text-[#00e5ff]">echten Alltag.</span></SectionTitle>
      <SectionSub>Fertige Prompt-Beispiele zum Kopieren und Anpassen. Wähle den Bereich der zu dir passt.</SectionSub>

      {/* Everyday life image */}
      <LernenImage
        file="anwendungen.png"
        alt="KI findet ihren Platz im Alltag – am Schreibtisch, am Küchentisch, am Abend."
        testid="lernen-img-anwendungen"
        overlayPos="bottom"
        height="h-[200px] sm:h-[260px] lg:h-[300px]"
        caption={
          <figcaption className="absolute left-0 right-0 bottom-0 p-5 text-center z-20">
            <p className="text-[13px] text-white/75 font-outfit">
              Kein Büro-Werkzeug. <span className="text-[#00e5ff] font-semibold">Ein Alltagsbegleiter.</span>
            </p>
          </figcaption>
        }
      />
      <div className="mb-10" />

      <div className="flex flex-wrap gap-2 mb-8">
        {TABS.map(({ k, label, icon: Icon }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`lernen-tab-${k}`}
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-[13px] font-outfit font-semibold border transition-colors ${tab === k ? "border-[#00e5ff] bg-[#00e5ff]/10 text-[#00e5ff]" : "border-white/10 bg-black text-white/60 hover:border-white/30 hover:text-white/85"}`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12 lernen-stagger" key={tab}>
        {DATA[tab].map(([title, desc, prompt], i) => (
          <div key={title} className="border border-white/[0.07] bg-white/[0.015] p-5 flex flex-col hover:border-[#00e5ff]/30 hover:bg-white/[0.025] transition-colors">
            <div className="font-outfit font-bold text-white text-[15px] mb-1">{title}</div>
            <div className="text-[12.5px] text-white/45 mb-4">{desc}</div>
            <div className="flex-1 border-l-2 border-[#00e5ff]/50 pl-3 text-[13px] text-white/70 leading-relaxed italic mb-4">
              „{prompt}"
            </div>
            <button
              onClick={() => copyPrompt(prompt, `${tab}-${i}`)}
              data-testid={`lernen-uc-copy-${tab}-${i}`}
              className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-outfit font-semibold uppercase tracking-[0.14em] border border-[#00e5ff]/40 text-[#00e5ff] hover:bg-[#00e5ff]/10 transition-colors"
            >
              {copiedIdx === `${tab}-${i}` ? <><Check size={12} /> Kopiert</> : <><Clipboard size={12} /> Prompt kopieren</>}
            </button>
          </div>
        ))}
      </div>

      <Quiz id="q3" />
    </div>
  );
}

function Step4({ onFinished }) {
  const TIPS = [
    { icon: RotateCw, title: "Probier es nochmal anders", text: "Wenn die Antwort nicht passt – nicht aufgeben. Sag was dir nicht gefällt. Die KI macht es neu. Unbegrenzt oft." },
    { icon: Eye, title: "Kritisch bleiben", text: "KI kann Fehler machen – besonders bei Zahlen, Fakten, aktuellen Ereignissen. Nutze sie als Startpunkt, nicht als Endurteil." },
    { icon: ShieldCheck, title: "Keine sensiblen Daten", text: "Passwörter, Kontonummern, Kundendaten – nicht eingeben. Was du tippst, verlässt deinen Computer." },
    { icon: Target, title: "Kontext ist dein Joker", text: "Je mehr du über deine Situation erzählst, desto besser die Antwort. Wer bist du, was ist die Lage, was brauchst du genau." },
    { icon: Rocket, title: "Einfach anfangen", text: "Der einzige Fehler ist nicht zu starten. Du musst nicht alles verstehen. Fang mit einer echten Alltagsaufgabe an – heute." },
    { icon: TrendingUp, title: "Du wirst schnell besser", text: "Nach 10 Prompts fragst du schon anders. Nach 50 wie ein Profi. Lernen durch Tun – nicht durch Lesen." },
  ];
  const DOS = ["Kontext geben wer du bist", "Länge und Format vorgeben", "Nachfragen wenn etwas unklar ist", "Antwort als Entwurf behandeln", "Verschiedene Tools ausprobieren", "Mit echten Aufgaben starten", "Spaß daran haben"];
  const DONTS = ["Passwörter oder Bankdaten eingeben", "Alles blind übernehmen", "Aufgeben wenn's nicht klappt", "Zu vage Anfragen stellen", "Einmal probieren und aufhören", "Auf Perfektion warten"];
  const CHECKLIST = [
    "Tag 1: Bei einem KI-Tool kostenlos registrieren",
    "Tag 1: Erste Nachricht schicken: „Hallo, was kannst du für mich tun?\"",
    "Tag 2: Eine echte E-Mail aus dem Alltag schreiben lassen",
    "Tag 3: Dasselbe bei einem anderen Tool testen und vergleichen",
    "Tag 4: Eine Frage stellen die dich wirklich beschäftigt",
    "Tag 5: Den Prompt-Baukasten auf Seite 3 benutzen",
    "Tag 6: Einer anderen Person zeigen was du gemacht hast",
    "Tag 7: Lieblingstool als Lesezeichen – tägliche Gewohnheit",
  ];
  const [checked, setChecked] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("lernen.checklist") || "[]"));
    } catch { return new Set(); }
  });
  useEffect(() => {
    localStorage.setItem("lernen.checklist", JSON.stringify([...checked]));
  }, [checked]);

  const toggle = (i) => {
    setChecked((prev) => {
      const n = new Set(prev);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });
  };

  return (
    <div>
      <SectionLabel>Das Wichtigste zum Schluss</SectionLabel>
      <SectionTitle>Tipps die dich vom<br />Anfänger zum <span className="text-[#00e5ff]">Entdecker</span> machen.</SectionTitle>
      <SectionSub>Kein Regelwerk. Nur was wirklich hilft.</SectionSub>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.05] mb-14 border border-white/[0.06] lernen-stagger">
        {TIPS.map(({ icon: Icon, title, text }) => (
          <div key={title} className="bg-black p-6 hover:bg-white/[0.02] transition-colors">
            <Icon size={20} className="text-[#00e5ff] mb-3" strokeWidth={1.6} />
            <div className="font-outfit font-bold text-white text-[15px] mb-2">{title}</div>
            <p className="text-[13px] text-white/55 leading-relaxed">{text}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.05] mb-14 border border-white/[0.06]">
        <div className="bg-black p-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 size={18} className="text-[#2ecc71]" />
            <h3 className="font-outfit font-bold text-[#2ecc71] text-[15px] uppercase tracking-[0.12em]">Mach das</h3>
          </div>
          <ul className="space-y-2.5">
            {DOS.map((d) => (
              <li key={d} className="flex items-start gap-2 text-[13.5px] text-white/75">
                <Check size={14} className="text-[#2ecc71] shrink-0 mt-1" />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-black p-6">
          <div className="flex items-center gap-2 mb-4">
            <XCircle size={18} className="text-[#ef4444]" />
            <h3 className="font-outfit font-bold text-[#ef4444] text-[15px] uppercase tracking-[0.12em]">Lass das lieber</h3>
          </div>
          <ul className="space-y-2.5">
            {DONTS.map((d) => (
              <li key={d} className="flex items-start gap-2 text-[13.5px] text-white/75">
                <XCircle size={14} className="text-[#ef4444] shrink-0 mt-1" />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Checklist */}
      <div className="border border-[#00e5ff]/25 bg-gradient-to-br from-[#00e5ff]/[0.04] to-transparent p-6 sm:p-8 mb-14">
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle2 size={18} className="text-[#00e5ff]" />
          <h3 className="font-outfit font-bold text-white text-[16px] uppercase tracking-[0.1em]">7-Tage-Starter-Plan</h3>
        </div>
        <p className="text-[12px] text-white/40 mb-5">Deine erste Woche – Fortschritt wird lokal im Browser gespeichert.</p>
        <div className="space-y-2">
          {CHECKLIST.map((item, i) => {
            const isOn = checked.has(i);
            return (
              <button
                key={item}
                onClick={() => toggle(i)}
                data-testid={`lernen-check-${i}`}
                className={`w-full flex items-start gap-3 px-4 py-3 text-left border transition-colors ${isOn ? "border-[#2ecc71]/50 bg-[#2ecc71]/[0.05]" : "border-white/10 bg-black hover:border-white/25"}`}
              >
                <span className={`shrink-0 w-5 h-5 border flex items-center justify-center mt-0.5 ${isOn ? "border-[#2ecc71] bg-[#2ecc71]/20" : "border-white/20"}`}>
                  {isOn && <Check size={12} className="text-[#2ecc71]" strokeWidth={3} />}
                </span>
                <span className={`text-[13.5px] leading-relaxed ${isOn ? "text-white/55 line-through" : "text-white/85"}`}>
                  {item}
                </span>
              </button>
            );
          })}
        </div>
        <div className="mt-4 text-[11px] text-white/40 font-outfit uppercase tracking-[0.14em]">
          {checked.size} / {CHECKLIST.length} erledigt
        </div>
      </div>

      <Quiz id="q4" onCorrect={onFinished} />
    </div>
  );
}

/* ---------- Page shell ---------- */

export default function LernenPage() {
  const [step, setStep] = useState(0);
  const [completed, setCompleted] = useState(false);

  const goTo = useCallback((i) => {
    setStep(i);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const progressPct = ((step + 1) / STEP_LABELS.length) * 100;

  return (
    <div className="min-h-screen bg-black text-white antialiased selection:bg-[#00e5ff]/30 relative overflow-x-hidden">
      {/* Ambient cyan glow */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[50%] h-[55%] rounded-full blur-[120px]" style={{ background: "radial-gradient(circle, rgba(0,229,255,0.055) 0%, transparent 70%)" }} />
        <div className="absolute bottom-0 left-0 w-[40%] h-[45%] rounded-full blur-[120px]" style={{ background: "radial-gradient(circle, rgba(168,85,247,0.04) 0%, transparent 70%)" }} />
      </div>

      <div className="relative z-10">
        <Navbar />

        {/* Sticky step progress */}
        <div className="sticky top-14 sm:top-16 z-20 border-b border-white/[0.06] bg-black/70 backdrop-blur-lg">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between gap-4 mb-2">
              <span className="font-outfit text-[11px] uppercase tracking-[0.22em] text-white/50 font-semibold">
                Schritt <span className="text-[#00e5ff]">{step + 1}</span> / {STEP_LABELS.length}
              </span>
              <span className="font-outfit text-[12px] text-white/65 truncate">{STEP_LABELS[step]}</span>
            </div>
            <div className="h-[2px] bg-white/[0.07] overflow-hidden">
              <div className="h-full bg-[#00e5ff] transition-all duration-500" style={{ width: `${progressPct}%` }} data-testid="lernen-progress-fill" />
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          {/* Scope notice */}
          <div className="mb-8 border-l-2 border-[#00e5ff] bg-[#00e5ff]/[0.04] pl-4 pr-4 py-3" data-testid="lernen-scope-notice">
            <div className="font-outfit text-[10px] uppercase tracking-[0.28em] text-[#00e5ff]/85 font-semibold mb-1">
              Hinweis · Scope
            </div>
            <p className="text-[13.5px] text-white/75 leading-relaxed">
              Hier lernst du die <strong className="text-white">Basics von KI</strong> — Tools, Prompts, erste Anwendungen.
              Fortgeschrittene Themen wie <span className="text-[#00e5ff]">Creative-AI (Bild/Video/Audio)</span> und
              <span className="text-[#00e5ff]"> Vibe-Coding</span> bauen wir nach und nach aus.
              Wenn du ein Thema priorisiert sehen willst: schreib's kurz ins Board.
            </p>
          </div>

          {/* Step nav dots */}
          <div className="flex flex-wrap items-center gap-2 mb-10">
            {STEP_LABELS.map((label, i) => (
              <button
                key={label}
                onClick={() => goTo(i)}
                data-testid={`lernen-step-${i}`}
                className={`inline-flex items-center gap-2 px-3.5 py-1.5 text-[11px] uppercase tracking-[0.16em] font-outfit font-semibold border transition-colors ${step === i ? "border-[#00e5ff] bg-[#00e5ff]/10 text-[#00e5ff]" : i < step ? "border-[#2ecc71]/40 bg-[#2ecc71]/[0.04] text-[#2ecc71]/80" : "border-white/10 bg-black text-white/50 hover:border-white/30 hover:text-white/75"}`}
              >
                <span>{i + 1}</span>
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          {step === 0 && <Step0 goNext={() => goTo(1)} />}
          {step === 1 && <Step1 />}
          {step === 2 && <Step2 />}
          {step === 3 && <Step3 />}
          {step === 4 && <Step4 onFinished={() => setCompleted(true)} />}

          {/* Final CTA */}
          {step === 4 && completed && (
            <div className="mt-12 border border-[#00e5ff]/40 bg-gradient-to-br from-[#00e5ff]/[0.08] to-transparent overflow-hidden" data-testid="lernen-completion">
              {/* Trophy image banner */}
              <div className="relative overflow-hidden border-b border-[#00e5ff]/20">
                <img
                  src={`${IMG_BASE}/abschluss.png`}
                  alt="Geschafft – du bist bereit."
                  className="w-full h-[180px] sm:h-[240px] object-cover opacity-85 lernen-completion-img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent pointer-events-none" />
              </div>
              <div className="p-8 sm:p-10 text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 border-2 border-[#00e5ff] bg-[#00e5ff]/10 mb-5 lernen-trophy-bob">
                  <Trophy size={24} className="text-[#00e5ff]" strokeWidth={1.6} />
                </div>
                <h2 className="font-outfit font-black text-3xl sm:text-4xl tracking-tighter text-white mb-3">
                  Du bist bereit. <span className="text-[#00e5ff]">Trau dich.</span>
                </h2>
                <p className="text-[15px] text-white/65 max-w-xl mx-auto leading-relaxed mb-6">
                  Öffne jetzt einen Browser-Tab, geh auf eines der Tools und schreib deine erste echte Anfrage. Die KI-Reise beginnt mit einem einzigen Satz.
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <button onClick={() => goTo(0)} data-testid="lernen-restart" className="px-5 py-3 text-[12px] font-outfit font-bold uppercase tracking-[0.14em] bg-[#00e5ff] text-black hover:bg-white transition-colors">
                    Nochmal von vorne
                  </button>
                  <button onClick={() => goTo(2)} data-testid="lernen-to-prompts" className="px-5 py-3 text-[12px] font-outfit font-bold uppercase tracking-[0.14em] border border-[#00e5ff]/40 text-[#00e5ff] hover:bg-[#00e5ff]/10 transition-colors">
                  Zum Prompt-Baukasten
                </button>
              </div>
              </div>
            </div>
          )}

          {/* Page nav */}
          <div className="flex items-center justify-between mt-12 pt-6 border-t border-white/[0.06]">
            <button
              onClick={() => goTo(Math.max(0, step - 1))}
              disabled={step === 0}
              data-testid="lernen-prev"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-[12px] font-outfit font-semibold uppercase tracking-[0.14em] border border-white/10 text-white/70 hover:border-white/30 hover:text-white disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={14} /> Zurück
            </button>
            <div className="hidden sm:flex items-center gap-1.5">
              {STEP_LABELS.map((label, i) => (
                <button
                  key={label}
                  onClick={() => goTo(i)}
                  aria-label={`Schritt ${i + 1}`}
                  className={`w-2.5 h-2.5 border transition-colors ${step === i ? "border-[#00e5ff] bg-[#00e5ff]" : "border-white/20 hover:border-white/50"}`}
                />
              ))}
            </div>
            <button
              onClick={() => goTo(Math.min(STEP_LABELS.length - 1, step + 1))}
              disabled={step === STEP_LABELS.length - 1}
              data-testid="lernen-next"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-[12px] font-outfit font-semibold uppercase tracking-[0.14em] bg-[#00e5ff] text-black hover:bg-white disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
            >
              Weiter <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        .lernen-reveal {
          animation: lernen-fade-up 0.7s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        @keyframes lernen-fade-up {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* Staggered reveal for grids/cards inside each step */
        .lernen-stagger > * {
          opacity: 0;
          animation: lernen-fade-up 0.55s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .lernen-stagger > *:nth-child(1) { animation-delay: 40ms; }
        .lernen-stagger > *:nth-child(2) { animation-delay: 110ms; }
        .lernen-stagger > *:nth-child(3) { animation-delay: 180ms; }
        .lernen-stagger > *:nth-child(4) { animation-delay: 250ms; }
        .lernen-stagger > *:nth-child(5) { animation-delay: 320ms; }
        .lernen-stagger > *:nth-child(6) { animation-delay: 390ms; }

        /* Gentle trophy bob */
        .lernen-trophy-bob {
          animation: lernen-bob 3.2s ease-in-out infinite;
        }
        @keyframes lernen-bob {
          0%,100% { transform: translateY(0); box-shadow: 0 0 0 rgba(0,229,255,0); }
          50%     { transform: translateY(-4px); box-shadow: 0 0 24px rgba(0,229,255,0.35); }
        }

        /* Completion image slow ken-burns */
        .lernen-completion-img {
          animation: lernen-kenburns 14s ease-in-out infinite alternate;
        }
        @keyframes lernen-kenburns {
          from { transform: scale(1) translateX(0); }
          to   { transform: scale(1.08) translateX(-1.5%); }
        }

        @media (prefers-reduced-motion: reduce) {
          .lernen-reveal, .lernen-stagger > *, .lernen-trophy-bob, .lernen-completion-img {
            animation: none;
            opacity: 1;
            transform: none;
          }
        }
      `}</style>
    </div>
  );
}
